import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import { NewsletterForm } from "components";
import { INewsletterSubmitData, INewsletterOption } from "types";
import {
  clearNewsletterMeta,
  newsletterMetaSelector,
  newsletterSubscribe,
} from "components/newsletter-form/state";

export interface NewsletterFormContainerProps {
  id: string;
  newsletters: INewsletterOption[];
}

export const NewsletterFormContainer: React.FC<NewsletterFormContainerProps> = ({
  id,
  newsletters,
}) => {
  const {
    status,
    successMessage,
    successCodes,
    error,
    errorCodes,
  } = useSelector(newsletterMetaSelector(id));
  const dispatch = useDispatch();

  const onSubmit = (data: { [key: string]: any }) => {
    // because there can be different newsletter blocks on 1 page
    // we have to add a unique ID to fields
    // and parse it away before posting to API

    const chosenNewsletters = data[`audiences_${id}`]?.map((id: string) => {
      const chosenNewsletter = newsletters.find(
        (newsletter) => newsletter.id === id
      );

      return {
        unique_id: chosenNewsletter?.id,
        audience_id: chosenNewsletter?.audience,
        segment_id: chosenNewsletter?.segment || null,
      };
    });

    const cleanData: INewsletterSubmitData = {
      email: data[`email_${id}`],
      audiences: chosenNewsletters,
      consent: data[`optin_${id}`],
    };

    dispatch(newsletterSubscribe({ id, data: cleanData }));
  };

  const clearState = () => {
    dispatch(clearNewsletterMeta(id));
  };

  const getSuccessMessage = useCallback(() => {
    if (!successCodes?.length) {
      return successMessage;
    }

    const subscribedNewsletters: string | undefined = successCodes
      ?.map(
        (code) =>
          newsletters.find((newsletter) => newsletter.id === code)?.title
      )
      .filter((value) => !!value)
      .join(", ");

    if (!subscribedNewsletters || !subscribedNewsletters.length) return;

    return successMessage?.replace("@newsletters", subscribedNewsletters);
  }, [newsletters, successCodes, successMessage]);

  const getError = useCallback(() => {
    if (!errorCodes?.length) {
      return error;
    }

    const alreadySubscribedNewsletters: string | undefined = errorCodes
      ?.map(
        (code) =>
          newsletters.find((newsletter) => newsletter.id === code)?.title
      )
      .filter((value) => !!value)
      .join(", ");

    if (!alreadySubscribedNewsletters || !alreadySubscribedNewsletters.length)
      return;

    return (error as string)?.replace(
      "@newsletters",
      alreadySubscribedNewsletters
    );
  }, [newsletters, errorCodes, error]);

  return (
    <NewsletterForm
      id={id}
      onSubmit={onSubmit}
      status={status}
      successMessage={getSuccessMessage()}
      error={getError()}
      clearState={clearState}
      newsletters={newsletters}
    />
  );
};
